/**
 * One EEG data-collection session on one Waterway level, plus the store of finished session ZIPs.
 *
 * The flow and the data are the game library dashboard's validation session (myndland-game-lib
 * apps/dashboard/src/validation-ui.ts), driven by this app's screens instead of the dashboard's panels:
 *
 *   start → calibration (eyes open, the level dimmed behind an overlay) until 30 s of usable EEG
 *   → the player presses Begin in the game (`level_start`) → 30 s alpha windows, decision scoring, and the live
 *   EEG driving the game's focus signal → end → ZIP → upload, kept in IndexedDB until a matching receipt.
 *
 * On top of that, every session is written to this app's session ledger (server/ledger.ts) at start and end,
 * which is what tells the next visit which study session the subject is on.
 */
import {
  mountGame, uploadTimestamp, uploadTrainingZip, type GameEvent, type GameHandle,
} from "@myndland/game-core";
import { AlphaSession, ALPHA_SETTINGS, type AlphaWindow } from "../vendor/myndland-game-lib/dashboard/eeg/alpha.ts";
import { EegDevice } from "../vendor/myndland-game-lib/dashboard/eeg/device.ts";
import { openPendingStore, type PendingStore, type PendingUpload } from "../vendor/myndland-game-lib/dashboard/session/pending.ts";
import { newSessionId, SessionRecorder } from "../vendor/myndland-game-lib/dashboard/session/recorder.ts";
import { manifest as waterway } from "@myndland/game-waterway/manifest";
import type { ModeInfo, SessionUpsert } from "../shared/api.ts";
import { fitIframe, stopFit } from "./fit.ts";

export interface SessionPlan {
  subjectId: number;
  number: number;
  week: number;
  mode: ModeInfo;
}

export interface FinishedSession {
  plan: SessionPlan;
  sessionId: string;
  reason: string;
  durationMs: number;
  gameplayMs: number | null;
  samples: number;
  decisions: number;
  windows: number;
  simulated: boolean;
  /** Null when the ZIP could not be built (the error is in `error`). */
  item: PendingUpload | null;
  error: string | null;
  ledgerError: string | null;
}

type Active = {
  plan: SessionPlan;
  rec: SessionRecorder;
  alpha: AlphaSession;
  handle: GameHandle;
  levelId: string;
  beganPending: boolean;
  startedAt: number;
};

const PROBE_QUESTION = "Just before this question, where was your attention?";
const LAMBDA = ALPHA_SETTINGS.defaultLambda;

/** A file-name-safe session id that carries the study session number: S03-20261105-101530-k3f9. */
export function studySessionId(number: number, now = Date.now()): string {
  return newSessionId(now).replace(/^S-/, `S${String(number).padStart(2, "0")}-`);
}

async function putLedger(sessionId: string, body: SessionUpsert): Promise<void> {
  let last: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
        method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(body), signal: AbortSignal.timeout(10_000),
      });
      if (res.ok) return;
      const text = await res.text();
      last = new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      if (res.status < 500) break;
    } catch (e) { last = e; }
    await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
  }
  throw last instanceof Error ? last : new Error(String(last));
}

export class Collector {
  readonly device = new EegDevice();
  #stage: HTMLElement;
  #onChange: () => void;
  #session: Active | null = null;
  #packaging = false;
  #overlay: HTMLElement | null = null;
  #probe: { el: HTMLElement; shownAt: number } | null = null;
  #lastSignal: "good" | "bad" | null = null;

  #store: PendingStore | null = null;
  #durable = false;
  pending: PendingUpload[] = [];
  readonly uploading = new Set<string>();
  readonly ready: Promise<void>;

  constructor(stage: HTMLElement, onChange: () => void) {
    this.#stage = stage;
    this.#onChange = onChange;
    this.device.on("status", (s) => {
      this.#session?.rec.host("eeg_status", Date.now(), { phase: s.status.phase, detail: s.status.detail, battery: s.battery });
      this.#onChange();
    });
    this.device.on("block", (block) => this.#session?.rec.eeg(block));
    this.device.on("epoch", (epoch) => {
      const s = this.#session;
      if (!s) return;
      const before = s.alpha.phase;
      s.alpha.epoch(epoch);
      s.rec.epoch(epoch);
      if (before === "calibrating" && s.alpha.phase === "calibrated") this.#onCalibrated();
    });
    setInterval(() => this.#tick(), 1000);
    window.addEventListener("beforeunload", (e) => {
      if (this.#session || this.#packaging || this.uploading.size) { e.preventDefault(); e.returnValue = ""; }
    });
    this.ready = openPendingStore().then(async (r) => {
      this.#store = r.store; this.#durable = r.durable;
      try {
        const saved = await r.store.list();
        this.pending = [...saved, ...this.pending.filter((p) => !saved.some((s) => s.id === p.id))];
      } catch (e) { console.error("[session] could not read the stored session ZIPs:", e); }
      this.#onChange();
    });
  }

  get active(): boolean { return !!this.#session; }
  get packaging(): boolean { return this.#packaging; }
  get durable(): boolean { return this.#durable; }
  get probing(): boolean { return !!this.#probe; }
  get plan(): SessionPlan | null { return this.#session?.plan ?? null; }

  /** Live state for the play screen. */
  snapshot() {
    const s = this.#session;
    if (!s) return null;
    const now = Date.now();
    const a = s.alpha.snapshot(now);
    return {
      phase: a.phase,
      elapsedMs: now - s.startedAt,
      gameplayMs: s.rec.gameplayStartedAt ? now - s.rec.gameplayStartedAt : null,
      samples: s.rec.samples,
      decisions: s.rec.decisions,
      alpha: a,
    };
  }

  /* ---------- session ---------- */

  /** Starts recording and loads the level. The headset must be streaming. */
  start(plan: SessionPlan): string {
    const info = this.device.info;
    if (this.#session || this.#packaging) throw new Error("A session is already running.");
    if (!info || !this.device.snapshot.streaming) throw new Error("The headset is not streaming.");
    const now = Date.now();
    const sessionId = studySessionId(plan.number, now);
    const alpha = new AlphaSession({ lambda: LAMBDA });
    const rec = new SessionRecorder({
      subjectId: plan.subjectId, sessionId, program: waterway.validation!.program!, moduleName: plan.mode.moduleName, moduleType: plan.mode.moduleType,
      game: {
        id: waterway.id, name: waterway.name, version: waterway.version, level: plan.mode.levelId, levelTitle: plan.mode.title,
        gameLevel: waterway.levels.find((l) => l.id === plan.mode.levelId)?.validation?.gameLevel ?? null,
      },
      lambda: LAMBDA, device: info,
    }, alpha, now);
    rec.host("study_session", now, { session_number: plan.number, week: plan.week, level: plan.mode.levelId, module_name: plan.mode.moduleName });

    this.#stage.innerHTML = "";
    const levelId = plan.mode.levelId;
    // noNotes: the design notes under the board are for the team, not participants.
    const handle = mountGame(this.#stage, {
      src: `/games/waterway/levels/${encodeURIComponent(levelId)}?noNotes=1`,
      onEvent: (ev) => this.#onGameEvent(ev),
      attributes: { title: `Waterway ${plan.mode.title}`, scrolling: "no" },
    });
    handle.iframe.addEventListener("load", () => {
      fitIframe(handle.iframe, this.#stage);
      // Until calibration finishes the game runs its own simulation; the EEG takes over after.
      if (!this.#drivingFocus()) { handle.clearFocus(); handle.setSignal("good"); }
    });

    this.#session = { plan, rec, alpha, handle, levelId, beganPending: false, startedAt: now };
    this.#lastSignal = null;
    alpha.startCalibration(now);
    rec.host("calibration_start", now, { required_usable_seconds: ALPHA_SETTINGS.calibrationUsableSeconds });
    this.#showCalibration();

    void putLedger(sessionId, {
      subjectId: plan.subjectId, number: plan.number, levelId, moduleName: plan.mode.moduleName, startedAt: now, gameplay: false, simulated: info.simulated,
    }).catch((e) => console.warn("[ledger] could not record the session start (the end is recorded again):", e));
    this.#onChange();
    return sessionId;
  }

  async end(reason: string): Promise<FinishedSession | null> {
    const s = this.#session;
    if (!s || this.#packaging) return null;
    this.#session = null;
    this.#lastSignal = null;
    this.#hideOverlay();
    if (this.#probe) this.#closeProbe("closed-by-session-end", s);
    const now = Date.now();
    for (const w of s.alpha.tick(now)) this.#onWindow(s, w);
    s.alpha.end(now);
    for (const w of s.alpha.windows.slice(-1).filter((w) => w.partial)) this.#onWindow(s, w);
    s.rec.end(reason, now);
    stopFit();
    s.handle.destroy();
    this.#stage.innerHTML = "";
    this.#packaging = true;
    this.#onChange();

    const m = s.rec.meta;
    const gameplayAt = s.rec.gameplayStartedAt;
    const result: FinishedSession = {
      plan: s.plan, sessionId: m.sessionId, reason, durationMs: now - s.startedAt, gameplayMs: gameplayAt ? now - gameplayAt : null,
      samples: s.rec.samples, decisions: s.rec.decisions, windows: s.alpha.windows.length, simulated: m.device.simulated,
      item: null, error: null, ledgerError: null,
    };
    const ledger = putLedger(m.sessionId, {
      subjectId: s.plan.subjectId, number: s.plan.number, levelId: s.levelId, moduleName: m.moduleName,
      startedAt: s.startedAt, endedAt: now, endReason: reason, gameplay: gameplayAt !== null, simulated: m.device.simulated,
    }).catch((e) => { result.ledgerError = e instanceof Error ? e.message : String(e); });
    try {
      const pkg = await s.rec.package();
      const item: PendingUpload = {
        id: m.sessionId, filename: pkg.filename, zip: pkg.zip, createdAt: now, subjectId: m.subjectId, sessionId: m.sessionId,
        program: m.program, moduleName: m.moduleName, moduleType: m.moduleType, simulated: m.device.simulated,
        message: m.device.simulated ? "Simulated EEG: download only, never uploaded." : "Waiting to upload.", retryable: !m.device.simulated,
      };
      this.pending.push(item);
      await this.#persist(item);
      result.item = item;
      console.info(`[session] ended (${reason}): ${pkg.filename}, ${(pkg.zip.length / 1048576).toFixed(2)} MiB, ${s.rec.samples} EEG samples, ${s.rec.decisions} decisions`);
    } catch (e) {
      console.error("[session] could not package the session:", e);
      result.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.#packaging = false;
    }
    await ledger;
    this.#onChange();
    if (result.item && !result.item.simulated) void this.upload(result.item);
    return result;
  }

  #onCalibrated() {
    const s = this.#session;
    if (!s) return;
    const c = s.alpha.calibration!;
    s.rec.host("calibration_end", c.endedAt, c);
    this.#hideOverlay();
    if (s.beganPending) this.#beginGameplay(s, Date.now());
    this.#onChange();
  }

  #beginGameplay(s: Active, t: number) {
    if (s.alpha.startGameplay(t)) {
      s.rec.markGameplayStart(t);
      s.rec.host("gameplay_start", t);
      void putLedger(s.rec.meta.sessionId, {
        subjectId: s.plan.subjectId, number: s.plan.number, levelId: s.levelId, moduleName: s.plan.mode.moduleName,
        startedAt: s.startedAt, gameplay: true, simulated: s.rec.meta.device.simulated,
      }).catch((e) => console.warn("[ledger] could not record gameplay start (the end is recorded again):", e));
    }
  }

  #onWindow(s: Active, w: AlphaWindow) { s.rec.host("alpha_window", w.tEnd, w); }

  /** Every event from the session's level goes into its record. */
  #onGameEvent(ev: GameEvent) {
    const s = this.#session;
    if (!s || ev.game !== waterway.id || ev.level !== s.levelId) return;
    if (ev.type === "error") console.error(`[game] ${ev.message}`);
    if (ev.type === "ready" && !this.#drivingFocus()) { s.handle.clearFocus(); s.handle.setSignal("good"); }
    if (ev.type === "marker" && ev.action === "level_start") {
      if (s.alpha.phase === "calibrated") this.#beginGameplay(s, ev.t);
      else if (s.alpha.phase === "calibrating") s.beganPending = true;
    }
    s.rec.game(ev);
  }

  /* ---------- live focus ---------- */
  /** After calibration the live EEG, not the game's own simulation, supplies the focus signal. */
  #drivingFocus(): boolean {
    const a = this.#session?.alpha.phase;
    return a === "calibrated" || a === "gameplay";
  }

  #tick() {
    const s = this.#session;
    if (!s) return;
    const now = Date.now();
    for (const w of s.alpha.tick(now)) this.#onWindow(s, w);
    if (this.#overlay?.dataset.kind === "calibration") this.#updateCalibration();
    if (this.#drivingFocus()) {
      const live = s.alpha.snapshot(now).liveFocus;
      const quality = live === null ? "bad" : "good";
      if (live !== null) s.handle.setFocus(live);
      if (quality !== this.#lastSignal) {
        s.handle.setSignal(quality);
        s.rec.host("signal", now, { quality, reason: quality === "bad" ? "no usable EEG in the last 4 s" : "usable EEG" });
        this.#lastSignal = quality;
      }
    }
    this.#onChange();
  }

  /* ---------- overlays ---------- */
  #showOverlay(kind: string, html: string): HTMLElement {
    this.#hideOverlay();
    const el = document.createElement("div");
    el.className = "stage-overlay";
    el.dataset.kind = kind;
    el.innerHTML = `<div class="card">${html}</div>`;
    this.#stage.appendChild(el);
    this.#overlay = el;
    return el;
  }
  #hideOverlay() { this.#overlay?.remove(); this.#overlay = null; }

  #showCalibration() {
    this.#showOverlay("calibration", `<div class="fix" aria-hidden="true">+</div><h2>Calibration</h2>
      <p>Keep your eyes open and look at the screen. Sit still and relax your face and jaw.</p>
      <div class="bar" role="progressbar" aria-label="Usable EEG collected" aria-valuemin="0" aria-valuemax="${ALPHA_SETTINGS.calibrationUsableSeconds}"><i></i></div>
      <div class="sub" data-sub></div>`);
    this.#updateCalibration();
  }
  #updateCalibration() {
    const s = this.#session, o = this.#overlay;
    if (!s || !o) return;
    const snap = s.alpha.snapshot(Date.now());
    const need = ALPHA_SETTINGS.calibrationUsableSeconds, got = Math.min(need, snap.calibrationUsableSeconds);
    o.querySelector<HTMLElement>(".bar i")!.style.width = `${(100 * got) / need}%`;
    o.querySelector(".bar")!.setAttribute("aria-valuenow", String(got));
    const sub = o.querySelector<HTMLElement>("[data-sub]")!;
    const elapsed = snap.calibrationElapsedSeconds;
    const slow = elapsed > need + 15 && got < need * (elapsed / (need + 15));
    sub.textContent = `${got} of ${need} s usable EEG · ${elapsed.toFixed(0)} s elapsed${slow ? " · noise or poor contact is extending calibration; check the headband fit" : ""}`;
    sub.classList.toggle("warn", slow);
  }

  /** Pauses the game and asks where the player's attention was (gameplay only). */
  showProbe() {
    const s = this.#session;
    if (!s || s.alpha.phase !== "gameplay" || this.#probe) return;
    const shownAt = Date.now();
    s.handle.post({ type: "pause" });
    s.rec.host("probe_shown", shownAt, { question: PROBE_QUESTION });
    const el = document.createElement("div");
    el.className = "stage-overlay";
    el.dataset.kind = "probe";
    el.innerHTML = `<div class="card"><h2>${PROBE_QUESTION}</h2><div class="choices">
      <button class="btn" data-a="on-task">On the game</button><button class="btn" data-a="mind-wandering">On something else</button></div></div>`;
    el.querySelectorAll<HTMLButtonElement>("button[data-a]").forEach((b) => b.addEventListener("click", () => this.#closeProbe(b.dataset.a!, s)));
    this.#stage.appendChild(el);
    this.#probe = { el, shownAt };
    this.#onChange();
  }
  #closeProbe(answer: string, s: Active) {
    if (!this.#probe) return;
    const now = Date.now(), rt = now - this.#probe.shownAt;
    s.rec.probe(now, answer, rt, PROBE_QUESTION);
    this.#probe.el.remove();
    this.#probe = null;
    s.handle.post({ type: "resume" });
    this.#onChange();
  }

  /* ---------- uploads ---------- */
  async #persist(item: PendingUpload) {
    try { await this.#store?.put(item); } catch (e) {
      console.error("[session] could not keep the session ZIP locally:", e);
      item.message = `Could not keep this ZIP in the browser (${e instanceof Error ? e.message : e}). Download it now.`;
    }
  }

  async upload(item: PendingUpload): Promise<void> {
    if (item.simulated || this.uploading.has(item.id)) return;
    this.uploading.add(item.id);
    item.message = "Uploading…";
    this.#onChange();
    try {
      const result = await uploadTrainingZip({
        program: item.program, moduleName: item.moduleName, moduleType: item.moduleType, subjectId: item.subjectId,
        uploadTimestamp: uploadTimestamp(Date.now()), zip: item.zip, filename: item.filename,
      }, async (form) => {
        const res = await fetch("/api/validation/game_recording", { method: "POST", body: form, signal: AbortSignal.timeout(150_000) });
        return { status: res.status, text: await res.text() };
      });
      item.message = result.message;
      item.retryable = result.retryable;
      if (result.confirmed && result.response.receipt) {
        const receipt = result.response.receipt;
        item.receipt = receipt;
        await fetch(`/api/sessions/${encodeURIComponent(item.sessionId)}/upload`, {
          method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ uploadId: receipt.id, status: receipt.status }),
        }).catch((e) => console.warn("[ledger] uploaded, but the receipt could not be recorded in the ledger:", e));
        await this.#store?.delete(item.id).catch((e) => console.error("[session] uploaded, but the local copy could not be removed:", e));
        this.pending = this.pending.filter((p) => p !== item);
      } else {
        await this.#persist(item);
      }
    } catch (e) {
      console.error("[session] upload failed:", e);
      item.message = `Not uploaded: ${e instanceof Error ? e.message : e}`;
      item.retryable = false;
      await this.#persist(item);
    } finally {
      this.uploading.delete(item.id);
      this.#onChange();
    }
  }

  download(item: PendingUpload) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([item.zip as BlobPart], { type: "application/zip" }));
    a.download = item.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  async discard(item: PendingUpload): Promise<boolean> {
    if (!confirm(`Delete ${item.filename} from this browser? ${item.simulated ? "" : "It has not been stored in the validation database. "}Download it first if you need it.`)) return false;
    await this.#store?.delete(item.id).catch((e) => console.error("[session] could not delete the local ZIP:", e));
    this.pending = this.pending.filter((p) => p !== item);
    this.#onChange();
    return true;
  }
}
